import * as React from 'react';
import {
  Box,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { CTextField, CButton } from '../../../../components/common/Modal';

export const OracleEPMConfigurationStep = ({
  mode,
  version,
  applicationName,
  metadataExportName,
  retryInterval,
  maxRetries,
  availableVersions,
  createdSourceId,
  hostname,
  username,
  password,
  syncLoading,
  syncResult,
  configLoading,
  skipLoading,
  loadedEpmConfig,
  isValid,
  onVersionChange,
  onApplicationNameChange,
  onMetadataExportNameChange,
  onRetryIntervalChange,
  onMaxRetriesChange,
  onSyncMetadata,
  onSaveAndNext,
  onBack,
  onSkip
}) => {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'medium' }}>
          Oracle EPM Configuration
          {loadedEpmConfig && (
            <Chip 
              label="Configuration Loaded" 
              size="small" 
              color="success" 
              sx={{ ml: 2 }} 
            />
          )}
        </Typography>
        
        <FormControl sx={{ minWidth: 120 }} size="small" fullWidth>
          <InputLabel id="version-select-label">Version</InputLabel>
          <Select
            labelId="version-select-label"
            id="version-select"
            value={version}
            label="Version"
            onChange={onVersionChange}
            disabled={availableVersions.length === 0}
          >
            {availableVersions.length === 0 ? (
              <MenuItem value="" disabled>
                Test connection to load versions
              </MenuItem>
            ) : (
              availableVersions.map((versionInfo, index) => (
                <MenuItem key={index} value={versionInfo.ref || versionInfo.href}>
                  {versionInfo.version}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
        
        <Box sx={{ display: 'flex', gap: 1, marginTop: '1rem' }}>
          <CTextField
            id="applicationName"
            label="Application Name"
            size="small"
            variant="outlined"
            value={applicationName}
            onChange={onApplicationNameChange}
            sx={{ flex: 1 }}
          />
          <CTextField
            id="metadataExportName"
            label="Metadata Export Name"
            size="small"
            variant="outlined"
            value={metadataExportName}
            onChange={onMetadataExportNameChange}
            sx={{ flex: 1 }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, marginTop: '1rem' }}>
          <CTextField
            id="retryInterval"
            label="Retry Interval"
            size="small"
            variant="outlined"
            value={retryInterval}
            onChange={onRetryIntervalChange}
            sx={{ flex: 1 }}
          />
          <CTextField
            id="maxRetries"
            label="Max Number of Retries"
            size="small"
            variant="outlined"
            value={maxRetries}
            onChange={onMaxRetriesChange}
            sx={{ flex: 1 }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <CButton 
            variant="outlined" 
            size="small"
            onClick={onSyncMetadata}
            disabled={!createdSourceId || !hostname || !username || !password || !version || !applicationName || !metadataExportName || syncLoading}
            startIcon={syncLoading ? <CircularProgress size={16} /> : null}
          >
            {syncLoading ? 'Syncing...' : 'Sync MetaData'}
          </CButton>
        </Box>
        
        {syncResult && (
          <Alert 
            severity={syncResult.success ? "success" : "error"} 
            sx={{ marginTop: '1rem' }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {syncResult.message}
            </Typography>
            {syncResult.detail && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 'medium', display: 'block', mb: 0.5 }}>
                  Error Details:
                </Typography>
                <Box 
                  component="pre" 
                  sx={{ 
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    padding: 1,
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: '200px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {syncResult.detail}
                </Box>
              </Box>
            )}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <CButton
            variant="outlined"
            onClick={onBack}
          >
            Back
          </CButton>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {mode === 'Edit' && (
              <CButton
                variant="outlined"
                onClick={onSkip}
                disabled={skipLoading}
                startIcon={skipLoading ? <CircularProgress size={16} /> : null}
              >
                {skipLoading ? 'Loading...' : 'Skip'}
              </CButton>
            )}
            <CButton
              variant="contained"
              onClick={onSaveAndNext}
              disabled={!isValid || configLoading}
              startIcon={configLoading ? <CircularProgress size={16} /> : null}
            >
              {configLoading ? 'Saving...' : mode === 'Edit' ? 'Update and Next' : 'Save and Next'}
            </CButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

